/** 回收站 */
import { EllipsisSpan } from '@flowus/common/components/ellipsis-span';
import { Highlight } from '@flowus/common/components/hight-light';
import { cx } from '@flowus/common/cx';
import type { BlockSearchDTO } from '@next-space/fe-api-idl';
import { BlockStatus, BlockType } from '@next-space/fe-api-idl';
import { debounce } from 'lodash-es';
import type { FC, MouseEvent, MutableRefObject } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { useHistory } from 'react-router-dom';
import { Icon } from 'src/common/components/icon';
import { Input } from 'src/common/components/input';
import { LoadingIcon } from 'src/common/components/loading-icon';
import { message } from 'src/common/components/message';
import { useCloseModal, useOpenModal } from 'src/common/components/next-modal';
import { PageNavigator } from 'src/common/components/page-navigator';
import { Tooltip } from 'src/common/components/tooltip';
import { ILLEGAL_TEXT } from 'src/common/const';
import { formatFileTimestamp } from 'src/common/utils/formatter';
import { BlockDefaultIcon } from 'src/components/block-default-icon';
import { useOpenRestoreTip } from 'src/components/delete-bar';
import { useOpenFilePreview } from 'src/components/file-preview/use-open-file-preview';
import { IconTrigger } from 'src/components/icon-trigger';
import { ImagesProvider } from 'src/components/images-provider';
import { useOpenCompleteDelete } from 'src/components/open-complete-delete';
import { segmentsToText } from 'src/editor/utils/editor';
import { useGetBreadcrumb } from 'src/hooks/block/use-navs';
import { useFetchSpaceCapacity } from 'src/hooks/drive/use-fetch-space-capacity';
import { useGetTrashBlock, useSearchBlock } from 'src/hooks/drive/use-search-block';
import { useDeletePageCompletely, useRestorePage } from 'src/hooks/page';
import { useOpenPage } from 'src/hooks/page/use-open-page';
import { usePermissions } from 'src/hooks/share/use-permissions';
import { useCurrentSpace } from 'src/hooks/space';
import { MessageIds, Modals } from 'src/modals';
import { getState } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import { setAppUiState } from 'src/services/app';
import { bizTracker } from 'src/utils/biz-tracker';
import { getUntitledName } from 'src/utils/get-untitled-name';
import { useGetPageId } from 'src/utils/getPageId';
import { bindDataTestId, TestIds } from 'src/utils/qa-utils';
import { useCloseRight, useGetRightPageId } from 'src/utils/right-utils';

const MAX_RESULT = 100;
interface State {
  view: 'SEARCH' | 'NOT_FOUND' | 'RESULT';
  loading: boolean;
  keyword: string;
  result: {
    total: number;
    items: BlockSearchDTO[];
    more: boolean;
  };
}

/** 回收站弹层 */
export const TrashPopup: FC = () => {
  const [state, setState] = useState<State>({
    view: 'RESULT',
    keyword: '',
    loading: false,
    result: {
      total: 0,
      items: [],
      more: false,
    },
  });
  const currentSpace = useCurrentSpace();
  const currentPageIndex = useRef(1);
  const searchBlock = useSearchBlock();
  const getTrashBlock = useGetTrashBlock();
  const openModal = useOpenModal();
  const deletePageCompletely = useDeletePageCompletely();
  const fetchSpaceCapacity = useFetchSpaceCapacity();

  const patchState = <K extends keyof State>(key: K, value: State[K]) => {
    if (key === 'keyword') {
      patchState('view', 'SEARCH');
      void patchResult(value as string);
    }
    setState((s) => ({
      ...s,
      [key]: value,
    }));
  };

  const search = async (space: string, page: number, perPage: number, search?: string) => {
    const result = await searchBlock(
      {
        spaceId: space,
        pageNo: page,
        perPage,
        search: search ?? '',
        fileType: undefined,
        type: undefined,
        source: 'trash',
      },
      true
    );

    currentPageIndex.current = result.newPageNo;
    return {
      more: result.hasMore,
      total: result.total,
      results: result.resultBlocks,
    };
  };

  const getTrash = async (space: string, pageNo: number, perPage: number) => {
    const result = await getTrashBlock(space, pageNo, perPage);
    currentPageIndex.current = result.newPageNo;
    return {
      more: result.hasMore,
      total: result.total,
      results: result.resultBlocks,
    };
  };

  const patchApi = async (keyword: string, pageIndex = 1, pageSize = 30) => {
    const spaceId = currentSpace.uuid;

    const postApi = keyword ? search : getTrash;

    const { total, results, more } = await postApi(spaceId, pageIndex, pageSize, keyword);

    const items = pageIndex === 1 ? [] : [...state.result.items];
    items.push(...results);

    if (total) {
      patchState('result', { total, items, more });
      patchState('view', 'RESULT');
    } else {
      patchState('result', { total: 0, items: [], more });
      patchState('view', 'NOT_FOUND');
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const patchResult = useMemo(() => debounce(patchApi, 800), []);

  useEffect(() => {
    patchState('keyword', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAttachBottom = async () => {
    if (state.loading) return;
    patchState('loading', true);
    currentPageIndex.current = currentPageIndex.current + 1;
    await patchApi(state.keyword, currentPageIndex.current);
    patchState('loading', false);
  };

  const filterSubNodes = (filterId: string) => {
    patchState('result', {
      ...state.result,
      items: state.result.items.filter((i) => i.block.uuid !== filterId),
    });
    // 更当前列表【页面*数量】，保证当前列表和后端数据库分页结果是同步的
    void patchApi(state.keyword as string, 1, currentPageIndex.current * 15);
  };

  const clear = () => {
    openModal.warning({
      title: '确定清空回收站？',
      confirmText: '确定',
      cancelText: '取消',
      confirm: async () => {
        patchState('result', {
          ...state.result,
          items: [],
        });
        patchState('loading', true);
        patchState('view', 'SEARCH');
        await deletePageCompletely(state.result.items.map((i) => i.block.uuid));
        await patchApi(state.keyword as string, 1);
        message.closeMessage(MessageIds.NO_PERMISSION_SYNC_WARNING_ID);
        setAppUiState({ $syncUpFault: { backup: false, noPermission: false } });
        void fetchSpaceCapacity();
        patchState('loading', false);
      },
    });
  };

  const container = useRef() as MutableRefObject<HTMLDivElement>;

  const recordDesc = state.result.total > MAX_RESULT ? `${MAX_RESULT}+` : state.result.total;

  return (
    <ImagesProvider uuid={currentSpace.uuid}>
      <div className="w-[638px] h-[608px] flex flex-col next-modal">
        <div className="m-2.5">
          <Input
            autoFocus
            className="w-full h-8"
            placeholder="在回收站中搜索"
            value={state.keyword}
            onChange={(value) => patchState('keyword', value)}
          />
        </div>
        {state.view === 'SEARCH' && (
          <div className="h-[46px] flex items-center justify-center text-grey3">
            <LoadingIcon size="middle" />
            <div className="ml-1 text-t2">正在搜索...</div>
          </div>
        )}
        {state.view === 'RESULT' &&
          (state.result.items.length !== 0 ? (
            <>
              <div className="h-[46px] flex items-center text-t2 text-grey3">
                {state.keyword ? (
                  <div className="w-full flex items-center px-4 justify-between text-grey3">
                    <div>搜索结果</div>
                    <div className="text-grey4">
                      <span className="text-grey3">{recordDesc}</span>
                      &nbsp;条记录
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="ml-[15px] w-[330px]">页面</div>
                    <div hidden={!!state.keyword}>删除时间</div>
                  </>
                )}
              </div>
              <div className="flex-1 overflow-y-auto" ref={container}>
                <InfiniteScroll
                  pageStart={1}
                  initialLoad={false}
                  useWindow={false}
                  loadMore={handleAttachBottom}
                  getScrollParent={() => container.current}
                  hasMore={state.result.more}
                >
                  {state.result.items.map((item, i) => (
                    <TrashItem
                      i={i}
                      key={item.block.uuid}
                      activeIndex={-1}
                      keyword={state.keyword}
                      filterSubNodes={filterSubNodes}
                      item={item}
                    />
                  ))}
                </InfiniteScroll>
                {state.loading && (
                  <div className="h-[46px] flex items-center justify-center text-grey3">
                    <LoadingIcon size="middle" />
                    <div className="ml-1 text-t2">正在加载下一页...</div>
                  </div>
                )}
                {!state.result.more && (
                  <div className="h-[46px] flex items-center justify-center text-grey3">
                    <div className="ml-1 text-t2">到底了...</div>
                  </div>
                )}
                {!state.loading && state.result.items.length === 0 && (
                  <div>
                    <div className="text-h0">👀</div>
                    <div>回收站内还没有页面</div>
                  </div>
                )}
              </div>
              {state.result.items.length > 0 && (
                <div className="h-10 flex items-center text-t2 text-grey3 px-2 border-t border-grey6">
                  <span className="px-2 animate-hover animate-click" onClick={clear}>
                    彻底删除当前列表的所有文件
                  </span>
                </div>
              )}
            </>
          ) : (
            !state.loading && (
              <div className="w-full flex flex-col items-center justify-center h-full text-t1">
                <div className="text-h0">👀</div>
                <div>回收站内还没有页面</div>
              </div>
            )
          ))}
        {state.view === 'NOT_FOUND' && (
          <div className="flex flex-col items-center justify-center pt-[184px] pointer-events-none">
            <div className="text-[70px] leading-[80px]">👀</div>
            <div className="text-grey4 text-t1">
              {state.keyword ? '未找到相关页面' : '回收站为空'}
            </div>
          </div>
        )}
      </div>
    </ImagesProvider>
  );
};

/** 回收站item */
const TrashItem: FC<{
  i: number;
  activeIndex: number;
  keyword: string;
  filterSubNodes: (filterId: string) => void;
  item: BlockSearchDTO;
}> = (props) => {
  const { i, activeIndex, keyword, item, filterSubNodes, ...rest } = props;

  const openFilePreview = useOpenFilePreview();
  const history = useHistory();
  const { block } = item;
  const { illegal } = usePermissions(item.block.uuid);

  const title = useMemo(() => {
    const title = segmentsToText(block.data.segments);
    if (title) return title;
    return getUntitledName(block.type);
  }, [block]);
  // 有可能是父页面被删，所以不需要做这个判断
  // if (block.status === BlockStatus.NORMAL) return null;

  const handleOpen = () => {
    bizTracker.event('recycle_pageclick');
    if (block.type === BlockType.FILE) {
      openFilePreview(block.uuid);
    } else {
      history.push(`/${block.uuid}`);
    }
  };

  return (
    <div
      {...rest}
      className={cx('mx-2 py-[9px] px-[7px] flex flex-row items-center animate-hover rounded', {
        'normal-bg': i === activeIndex,
      })}
      onClick={handleOpen}
    >
      <div className={cx('flex flex-row', !keyword ? 'w-[330px]' : 'w-full')}>
        <div className="text-xl leading-none block">
          <IconTrigger
            blockId={item.block.uuid}
            trigger={false}
            defaultIcon={<BlockDefaultIcon uuid={item.block.uuid} />}
          />
        </div>
        <div
          className={cx(
            'ml-2 flex-1 max-w-[280px]',
            !item.navs.length && 'flex flex-col justify-center'
          )}
        >
          {/* <EllipsisSpan className="text-t2 block">{illegal ? ILLEGAL_TEXT : title}</EllipsisSpan> */}
          <EllipsisSpan className="text-t2 block">
            <Highlight
              text={illegal ? ILLEGAL_TEXT : title} // 为什么垃圾箱还能搜出被风控的
              search={keyword}
              className="text-t2-bold"
            />
          </EllipsisSpan>
          <div className="mt-[2px] text-t4 text-grey3" hidden={!item.navs.length}>
            <PageNavigator navs={item.navs} />
          </div>
        </div>
      </div>
      <div hidden={!!keyword} className="text-t2 flex-1 text-grey2">
        {item.block.deletedAt ? formatFileTimestamp(item.block.deletedAt) : '-'}
      </div>
      <div>
        <ControlButton
          filterSubNodes={filterSubNodes}
          block={block as NextBlock}
          uuid={item.block.uuid}
        />
      </div>
    </div>
  );
};

/** 操作按钮 copy from old */
export const ControlButton: FC<{
  uuid: string;
  filterSubNodes: (blockId: string) => void;
  block: NextBlock;
}> = ({ uuid, filterSubNodes, block }) => {
  const closeModal = useCloseModal();
  const history = useHistory();
  const restorePage = useRestorePage();
  const pageId = useGetPageId();
  const rightPageId = useGetRightPageId();
  const getBreadcrumb = useGetBreadcrumb();
  const openPage = useOpenPage();
  const openRestoreTip = useOpenRestoreTip();
  const openCompleteDelete = useOpenCompleteDelete(uuid);
  const closeRight = useCloseRight();

  const handleRestoreFile = (event: MouseEvent) => {
    bizTracker.event('recycle_recover', { page_source: 'fromTrash' });
    event.stopPropagation();
    const { blocks } = getState();
    const parentFolder = blocks[block.parentId];

    if (block.type === BlockType.FILE) {
      if (
        parentFolder?.status === BlockStatus.NORMAL &&
        (parentFolder.type === BlockType.FOLDER || parentFolder.type === BlockType.PAGE)
      ) {
        history.push(`/${parentFolder.uuid}`);
        closeModal(Modals.TRASH);
        void restorePage(block.uuid);
      } else {
        openRestoreTip({ uuid: block.uuid });
      }

      return;
    }

    if (
      (block.type === BlockType.FOLDER ||
        block.type === BlockType.COLLECTION_VIEW_PAGE ||
        block.type === BlockType.COLLECTION_VIEW ||
        block.type === BlockType.MIND_MAPPING ||
        block.type === BlockType.MIND_MAPPING_PAGE) &&
      parentFolder?.type === BlockType.COLLECTION_VIEW_PAGE
    ) {
      openRestoreTip({ uuid: block.uuid });
      return;
    }

    void restorePage(uuid);
    openPage(uuid);
    closeModal(Modals.TRASH);
  };

  const handleDeleteFile = async (event: MouseEvent) => {
    bizTracker.event('completely_delete', { page_source: 'fromTrash' });
    event.stopPropagation();

    openCompleteDelete(() => {
      filterSubNodes(uuid);
      const currentPageOriginNavs = getBreadcrumb(pageId, true);
      const rightPageOriginNavs = rightPageId && getBreadcrumb(rightPageId, true);
      const { blocks } = getState();
      if (
        (currentPageOriginNavs.find((nav) => nav.uuid === uuid) &&
          blocks[pageId]?.status !== BlockStatus.DELETED) ||
        pageId === uuid // 自身已删除的页面/文件夹不用跳转
      ) {
        closeRight();
      } else if (
        rightPageId === uuid ||
        (rightPageOriginNavs &&
          currentPageOriginNavs.find((nav) => nav.uuid === uuid) &&
          blocks[pageId]?.status !== BlockStatus.DELETED)
      ) {
        closeRight();
      }
    });
  };

  return (
    <div className="flex ">
      <Tooltip popup="恢复">
        <button
          {...bindDataTestId(TestIds.trashItemRestore)}
          className="flex items-center justify-center w-5 h-5 mr-2 rounded animate-hover group-hover:flex"
        >
          <Icon size="middle" name="IcUndo" onClick={handleRestoreFile} className="text-grey2/70" />
        </button>
      </Tooltip>
      <Tooltip popup="彻底删除">
        <button
          {...bindDataTestId(TestIds.trashItemDelete)}
          className="flex items-center justify-center w-5 h-5 rounded animate-hover group-hover:flex"
        >
          <Icon name="IcTrash" size="middle" onClick={handleDeleteFile} className="text-grey2/70" />
        </button>
      </Tooltip>
    </div>
  );
};
